import { useSyncExternalStore } from 'react'
import type { ProjectSnapshot } from '../application/repository'
import type { Recommendation, Role } from '../domain/models'
import { api, readSession, setAPIRole, type Session } from '../infrastructure/api'

export type QueuedCommand = {
  kind: string
  id: string
  payload: unknown
  commandId: string
  expectedRevision?: number
  createdAt: string
  userId: string
  role: string
  attempts?: number
  nextAttemptAt?: string
  status?: 'pending' | 'needs_fix' | 'conflict' | 'login_required'
  error?: string
}
const queueKey = 'magistral.pending-facts.v1'
const offlineKinds = new Set([
  'readiness.observation',
  'readiness.reading',
  'readiness.refuel',
])
const retrySeconds = [5, 15, 30, 60]
function queuedCommands(): QueuedCommand[] {
  if (typeof localStorage === 'undefined') return []
  try {
    const value = JSON.parse(localStorage.getItem(queueKey) || '[]')
    return Array.isArray(value)
      ? value.filter((item): item is QueuedCommand =>
          item && typeof item.commandId === 'string' && typeof item.userId === 'string' && typeof item.role === 'string')
      : []
  } catch {
    return []
  }
}
function saveQueue(queue: QueuedCommand[]) {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(queueKey, JSON.stringify(queue))
  emit()
}
export const pendingFacts = () => {
  const session = connection.session
  return session ? queuedCommands().filter((item) => item.userId === session.user.id && item.role === session.role) : []
}
export function retryPendingFact(commandId: string) {
  updatePendingFact(commandId, (item) => ({ ...item, status: 'pending', error: '', nextAttemptAt: new Date().toISOString() }))
  void flushQueuedCommands()
}
export function editPendingFact(commandId: string, payload: unknown) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) throw new Error('Заполните данные сообщения')
  updatePendingFact(commandId, (item) => {
    const before = item.payload as Record<string, unknown>
    const after = payload as Record<string, unknown>
    for (const field of ['equipmentId', 'shiftId', 'occurredAt', 'recordedAt']) {
      if (before?.[field] !== after[field]) throw new Error('Машину, смену и исходное время факта менять нельзя')
    }
    return { ...item, payload, status: 'pending', error: '', attempts: 0, nextAttemptAt: new Date().toISOString() }
  })
  void flushQueuedCommands()
}
export function deletePendingFact(commandId: string) {
  updatePendingFact(commandId, () => null)
}
function updatePendingFact(commandId: string, update: (item: QueuedCommand) => QueuedCommand | null) {
  const session = connection.session
  if (!session) throw new Error('Войдите в аккаунт, чтобы управлять очередью')
  if (syncingCommandId === commandId) throw new Error('Дождитесь завершения отправки')
  const queue = queuedCommands()
  const index = queue.findIndex((item) => item.commandId === commandId && item.userId === session.user.id && item.role === session.role)
  if (index < 0) throw new Error('Сообщение не найдено в очереди этого аккаунта')
  const next = update(queue[index])
  if (next) queue[index] = next
  else queue.splice(index, 1)
  saveQueue(queue)
  connection = { ...connection, pendingSync: pendingFor(queue, session) }
  emit()
  scheduleQueueRetry()
}
const pendingFor = (queue: QueuedCommand[], session: Session | null) =>
  session ? queue.filter((item) => item.userId === session.user.id && item.role === session.role).length : 0

function resumeAuthenticatedQueue(session: Session) {
  const queue = queuedCommands()
  let changed = false
  for (const item of queue) {
    if (item.userId === session.user.id && item.role === session.role && item.status === 'login_required') {
      item.status = 'pending'
      item.error = ''
      item.nextAttemptAt = new Date().toISOString()
      changed = true
    }
  }
  if (changed) saveQueue(queue)
}

const empty: ProjectSnapshot = {
  workOrders: [],
  pipelines: [],
  workers: [],
  contractors: [],
  purchases: [],
  notifications: [],
  imports: [],
  revision: 0,
  equipmentTypes: [],
  typeCatalog: [],
  equipment: [],
  operators: [],
  requests: [],
  fuelStock: [],
  consumables: [],
  artifacts: [],
  clients: [],
  sites: [],
  scheduleAssignments: [],
  shifts: [],
  movements: [],
  maintenanceHistory: [],
  events: [],
}
const asArray = <T>(value: readonly T[] | null | undefined): T[] => (Array.isArray(value) ? [...value] : [])

function normalizeSnapshot(data: ProjectSnapshot): ProjectSnapshot {
  return {
    ...empty,
    ...data,
    workOrders: asArray(data.workOrders).map((work) => ({
      ...work,
      materials: asArray(work.materials),
      actualMaterials: asArray(work.actualMaterials),
      assigneeIds: asArray(work.assigneeIds),
      stages: asArray(work.stages),
      operations: asArray(work.operations),
    })),
    requests: asArray(data.requests).map((request) => ({
      ...request,
      materials: asArray(request.materials),
    })),
    purchases: asArray(data.purchases).map((purchase) => ({
      ...purchase,
      lines: asArray(purchase.lines),
    })),
    equipment: asArray(data.equipment),
    operators: asArray(data.operators),
    consumables: asArray(data.consumables),
    fuelStock: asArray(data.fuelStock),
    scheduleAssignments: asArray(data.scheduleAssignments),
    workers: asArray(data.workers),
    pipelines: asArray(data.pipelines),
    contractors: asArray(data.contractors),
    notifications: asArray(data.notifications),
    imports: asArray(data.imports),
    equipmentTypes: asArray(data.equipmentTypes),
    typeCatalog: asArray(data.typeCatalog),
    artifacts: asArray(data.artifacts),
    clients: asArray(data.clients),
    sites: asArray(data.sites),
    shifts: asArray(data.shifts),
    movements: asArray(data.movements),
    maintenanceHistory: asArray(data.maintenanceHistory),
    events: asArray(data.events),
  }
}
let snapshot = empty
let connection: {
  loading: boolean
  error: string
  session: Session | null
  unauthorized: boolean
  pendingSync: number
} = {
  loading: true,
  error: '',
  session: null,
  unauthorized: false,
  pendingSync: 0,
}
const listeners = new Set<() => void>()
const subscribe = (fn: () => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}
function emit() {
  listeners.forEach((fn) => fn())
}
let generation = 0
let loadSequence = 0
export type RefreshResult = { ok: true } | { ok: false; error: string }
let pendingLoad: Promise<RefreshResult> | undefined
export const projectRepository = { getSnapshot: () => snapshot }
export const useProject = () => useSyncExternalStore(subscribe, projectRepository.getSnapshot)
export const useConnection = () => useSyncExternalStore(subscribe, () => connection)
export function clearSession() {
  generation++
  snapshot = empty
  connection = { loading: false, error: '', session: null, unauthorized: true, pendingSync: 0 }
  emit()
}
export function loadProject(role: Role, reset = false): Promise<RefreshResult> {
  // Manual refresh must await an active background load, not silently skip it.
  if (!reset && pendingLoad) return pendingLoad
  if (reset) generation++
  const turn = generation
  const sequence = ++loadSequence
  pendingLoad = readProject(role, reset, turn, sequence).finally(() => {
    if (sequence === loadSequence) pendingLoad = undefined
  })
  return pendingLoad
}
async function readProject(
  role: Role,
  reset: boolean,
  turn: number,
  sequence: number,
): Promise<RefreshResult> {
  const superseded = (): RefreshResult => ({
    ok: false,
    error: 'Контекст данных изменился. Повторите обновление.',
  })
  setAPIRole(role)
  if (reset) snapshot = empty
  const wasUnauthorized = connection.unauthorized
  connection = { ...connection, loading: true, error: '' }
  emit()
  try {
    const session = await readSession()
    if (turn !== generation || sequence !== loadSequence) return superseded()
    const data = await api<ProjectSnapshot>('/mechanization')
    if (turn !== generation || sequence !== loadSequence) return superseded()
    if (data.revision >= snapshot.revision) snapshot = normalizeSnapshot(data)
    resumeAuthenticatedQueue(session)
    connection = {
      loading: false,
      error: '',
      session,
      unauthorized: false,
      pendingSync: pendingFor(queuedCommands(), session),
    }
    emit()
    void flushQueuedCommands()
    return { ok: true }
  } catch (error) {
    if (turn !== generation || sequence !== loadSequence) return superseded()
    const unauthorized = (error as { status?: number }).status === 401
    if (unauthorized) snapshot = empty
    connection = {
      ...connection,
      loading: false,
      error: error instanceof Error ? error.message : 'Не удалось загрузить данные',
      unauthorized: unauthorized || wasUnauthorized,
      session: unauthorized ? null : connection.session,
    }
    emit()
    return { ok: false, error: connection.error }
  }
}
export async function command(
  kind: string,
  id: string,
  payload: unknown,
  commandId: string = crypto.randomUUID(),
  revision = snapshot.revision,
) {
  const turn = generation
  let data: ProjectSnapshot
  try {
    data = await api<ProjectSnapshot>('/mechanization/commands', {
      method: 'POST',
      body: JSON.stringify({ kind, id, payload, commandId, expectedRevision: revision }),
    })
  } catch (error) {
    if ((error as { status?: number }).status === 401 && turn === generation) clearSession()
    if ((error as { status?: number }).status === 0 && offlineKinds.has(kind) && connection.session?.role === 'operator') {
      const queue = queuedCommands()
      if (!queue.some((item) => item.commandId === commandId)) {
        queue.push({
          kind,
          id,
          payload,
          commandId,
          expectedRevision: revision,
          createdAt: new Date().toISOString(),
          userId: connection.session?.user.id || '',
          role: connection.session?.role || '',
          attempts: 0,
          nextAttemptAt: new Date(Date.now() + 5000).toISOString(),
          status: 'pending',
        })
        saveQueue(queue)
      }
      connection = { ...connection, error: '', pendingSync: pendingFor(queue, connection.session) }
      emit()
      scheduleQueueRetry()
      return { queued: true as const }
    }
    throw error
  }
  if (turn === generation && data.revision >= snapshot.revision) {
    snapshot = normalizeSnapshot(data)
    emit()
  }
}

let syncingQueue = false
let syncingCommandId = ''
let retryTimer: ReturnType<typeof setTimeout> | undefined
function scheduleQueueRetry() {
  if (typeof window === 'undefined') return
  if (retryTimer) clearTimeout(retryTimer)
  retryTimer = undefined
  const first = pendingFacts().find((item) => offlineKinds.has(item.kind))
  if (!first || (first.status && first.status !== 'pending')) return
  const next = new Date(first.nextAttemptAt || 0).getTime()
  if (!Number.isFinite(next)) return
  retryTimer = setTimeout(() => void flushQueuedCommands(), Math.max(0, next - Date.now()))
}
async function flushQueuedCommands() {
  if (syncingQueue || (typeof navigator !== 'undefined' && navigator.onLine === false)) return
  const ownerId = connection.session?.user.id
  const ownerRole = connection.session?.role
  if (!ownerId || !ownerRole) return
  const turn = generation
  syncingQueue = true
  try {
    while (true) {
      if (turn !== generation || connection.session?.user.id !== ownerId || connection.session?.role !== ownerRole) break
      const queue = queuedCommands()
      const itemIndex = queue.findIndex((item) =>
        item.userId === ownerId && item.role === ownerRole && offlineKinds.has(item.kind),
      )
      if (itemIndex < 0) break
      const item = queue[itemIndex]
      if ((item.status && item.status !== 'pending') || new Date(item.nextAttemptAt || 0).getTime() > Date.now()) break
      syncingCommandId = item.commandId
      try {
        const data = await api<ProjectSnapshot>('/mechanization/commands', {
          method: 'POST',
          body: JSON.stringify({
            kind: item.kind,
            id: item.id,
            payload: item.payload,
            commandId: item.commandId,
            expectedRevision: item.expectedRevision ?? snapshot.revision,
          }),
        })
        const latest = queuedCommands().filter((entry) => entry.commandId !== item.commandId)
        saveQueue(latest)
        if (turn === generation && connection.session?.user.id === ownerId && connection.session?.role === ownerRole) {
          if (data.revision >= snapshot.revision) snapshot = normalizeSnapshot(data)
          connection = { ...connection, error: '', pendingSync: pendingFor(latest, connection.session) }
          emit()
        }
      } catch (error) {
        const status = (error as { status?: number }).status ?? 0
        const message = error instanceof Error ? error.message : 'Ошибка синхронизации'
        const latest = queuedCommands()
        const current = latest.find((entry) => entry.commandId === item.commandId)
        if (current) {
          const attempts = (current.attempts || 0) + 1
          current.attempts = attempts
          current.error = message
          current.status = status === 401 ? 'login_required' : status === 409 ? 'conflict' : status >= 400 && status < 500 ? 'needs_fix' : 'pending'
          current.nextAttemptAt = current.status === 'pending'
            ? new Date(Date.now() + 1000 * (retrySeconds[Math.min(attempts - 1, retrySeconds.length - 1)])).toISOString()
            : undefined
          saveQueue(latest)
        }
        if (turn === generation && connection.session?.user.id === ownerId && connection.session?.role === ownerRole) {
          connection = {
            ...connection,
            error: `Не синхронизирован факт: ${message}`,
            pendingSync: pendingFor(queuedCommands(), connection.session),
          }
          emit()
        }
        if (status === 401 && turn === generation && connection.session?.user.id === ownerId && connection.session?.role === ownerRole) clearSession()
        break
      } finally {
        syncingCommandId = ''
      }
    }
  } finally {
    syncingQueue = false
    scheduleQueueRetry()
    if (turn !== generation && connection.session?.user.id) void flushQueuedCommands()
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void flushQueuedCommands())
}
export const recommend = (id: string) =>
  api<Recommendation>('/mechanization/requests/' + encodeURIComponent(id) + '/recommendations')
export async function uploadDocument(
  file: File,
  category: string,
  requestId = '',
  binding: { equipmentId?: string; operatorId?: string; workOrderId?: string } = {},
) {
  const form = new FormData()
  form.set('file', file)
  form.set('category', category)
  form.set('requestId', requestId)
  Object.entries(binding).forEach(([key, value]) => {
    if (value) form.set(key, value)
  })
  const turn = generation
  const data = await api<ProjectSnapshot>('/mechanization/documents', { method: 'POST', body: form })
  if (turn === generation && data.revision >= snapshot.revision) {
    snapshot = data
    emit()
  }
}

export async function previewImport(file: File, kind: string) {
  const turn = generation
  const form = new FormData()
  form.set('file', file)
  form.set('kind', kind)
  const result = await api<{
    preview: import('../domain/operations').ImportRecord
    snapshot: ProjectSnapshot
  }>('/mechanization/imports/preview', { method: 'POST', body: form })
  if (turn !== generation) throw new Error('Роль изменилась. Повторите импорт в кабинете диспетчера.')
  if (result.snapshot.revision >= snapshot.revision) {
    snapshot = result.snapshot
    emit()
  }
  return result.preview
}
